/**
 * Pure helpers for the V3 Credin creation screen (`CreateCredinV3Page`).
 *
 * The V3 screen renders the UI-kit `CreateCredin` component but keeps the legacy `debin`
 * business logic untouched: these mappers translate between the UI-kit shapes
 * (`CreateCredinAccountOption`, `RecipientEntry`, `RecipientAgendaContact`) and the legacy
 * `debin.cbuList` / seller contract consumed by `credin.create.preview` and the summary page.
 */
import * as config from "util/config";
import * as i18n from "util/i18n";
import * as transferMiddleware from "middleware/transfer";

/** CBU/CVU length used to decide whether a searched value is an account number or an alias. */
const CBU_CVU_LENGTH = 22;

/**
 * Logical label id → [i18n key, Spanish fallback]. `CreateCredin` resolves both its own copy and
 * the internal `RecipientsArrayField` keys through a single `getFormLabel(id)`.
 *
 * @type {Record<string, [string, string]>}
 */
const CREATE_CREDIN_LABELS = {
    pageTitle: ["requestCredin.title", "Generar CREDIN"],
    debitAccount: ["transfers.debitaccount.label", "Cuenta de débito"],
    debitAccountPlaceholder: ["transfers.productSelector.placeholder", "Seleccioná"],
    addRecipient: ["credin.create.addRecipient", "Añadir destinatario"],
    expandAgenda: ["credin.create.expandAgenda", "Desplegar agenda"],
    emptyRecipients: [
        "credin.create.emptyRecipients",
        "No tenés destinatarios agregados, debés agregar para poder continuar.",
    ],
    drawerTitle: ["credin.create.drawerTitle", "Añadir destinatario"],
    invalidFormat: ["credin.create.invalidFormat", "El formato ingresado no es válido."],
    duplicateRecipient: [
        "accounts.new.account.field.error.repeated",
        "El CBU/CVU/Alias ingresado ya existe",
    ],
    limitExceeded: ["transfers.limit.exceeded", "Límite excedido"],
    removeRecipient: ["credin.create.removeRecipient", "Quitar"],
    amount: ["transaction.amount.label", "Monto"],
    comprobante: ["accounts.movement.detail.voucher", "Comprobante"],
    totalOperations: ["transactions.selected.total.operations", "Operaciones totales"],
    totalCbuOperations: ["credin.create.totalCbuOperations", "Operaciones a CBU"],
    totalCvuOperations: ["credin.create.totalCvuOperations", "Operaciones a CVU"],
    concept: ["requestCredin.concept.label", "Concepto"],
    conceptPlaceholder: ["transfers.productSelector.placeholder", "Seleccioná"],
    description: ["credin.create.description", "Descripción"],
    descriptionPlaceholder: ["credin.create.descriptionPlaceholder", "Ingresá una descripción"],
    continue: ["global.continue", "Continuar"],
    saveDraft: ["transfers.actions.saveDraft", "Guardar borrador"],
};

/**
 * Single label resolver for the `CreateCredin` page and its `RecipientsArrayField`.
 *
 * @param {string} id
 * @returns {string}
 */
export function getCreateCredinLabel(id) {
    const entry = CREATE_CREDIN_LABELS[id];
    if (!entry) {
        return i18n.get(`credin.create.${id}`, id);
    }
    return i18n.get(entry[0], entry[1]);
}

/**
 * Masks an account number keeping the last 4 digits (`****1234`).
 *
 * @param {string|number|undefined|null} number
 * @returns {string}
 */
export function maskCredinAccountNumber(number) {
    const value = String(number ?? "").trim();
    if (value.length <= 4) {
        return value;
    }
    return `****${value.slice(-4)}`;
}

/**
 * Human currency label ("ARS", "USD"...) for a legacy account currency code ("0", "2", ...).
 *
 * @param {string|number|undefined|null} currencyCode
 * @returns {string}
 */
export function getCredinCurrencyLabel(currencyCode) {
    const key = String(currencyCode ?? "ARS");
    return i18n.get(`currency.label.${key}`, key === "2" ? "USD" : "ARS");
}

/**
 * Product-type description used by the account selector (mirrors the dashboard v3 mapping).
 *
 * @param {string|undefined|null} productType
 * @returns {string}
 */
function getCredinProductTypeLabel(productType) {
    if (productType === "CA") {
        return i18n.get("accounts.productType.savings", "Caja de ahorro");
    }
    return i18n.get("accounts.productType.checking", "Cuenta corriente");
}

/**
 * Maps legacy accounts (`reducers/accounts` → `getAccounts`) into UI-kit `accountSelect` options.
 *
 * @param {Array<object>} accounts
 * @returns {Array<object>} `accountSelect` options with shape
 * `{ id, accountName, description, currency, amount, codeBank }`.
 */
export function mapCredinDebitAccounts(accounts) {
    if (!Array.isArray(accounts)) {
        return [];
    }
    return accounts.map((account) => ({
        id: String(account.idProduct),
        accountName: account.productAlias || maskCredinAccountNumber(account.number),
        description: getCredinProductTypeLabel(account.productType),
        currency: getCredinCurrencyLabel(account.currency),
        amount: account.balance ?? account.countableBalance ?? 0,
        codeBank: account.cbu ? String(account.cbu).substring(0, 3) : "319",
    }));
}

/**
 * Finds the raw legacy account for a selected option id.
 *
 * @param {Array<object>} accounts
 * @param {string|undefined|null} accountId
 * @returns {object|undefined}
 */
export function findCredinAccount(accounts, accountId) {
    if (!Array.isArray(accounts) || accountId == null) {
        return undefined;
    }
    return accounts.find((account) => String(account.idProduct) === String(accountId));
}

/**
 * Concepts (`debin.concepts` → `{ id, name }`) into UI-kit select options `id - name`.
 *
 * @param {Array<{ id: string, name: string }>} concepts
 * @returns {Array<{ value: string, label: string }>}
 */
export function mapCredinConcepts(concepts) {
    if (!Array.isArray(concepts)) {
        return [];
    }
    return concepts.map(({ id, name }) => ({ value: id, label: `${id} - ${name}` }));
}

/**
 * Frequent destinations (`frequentDestination.listFrequentDestinations`) into the agenda contacts
 * consumed by `RecipientsArrayField`.
 *
 * @param {Array<object>} list
 * @returns {Array<object>}
 */
export function mapCredinAgendaContacts(list) {
    if (!Array.isArray(list)) {
        return [];
    }
    return list.map((contact) => ({
        id: contact.idFrequentDestination != null ? String(contact.idFrequentDestination) : contact.cbu,
        cbu: contact.cbu,
        owner: contact.recipientName || contact.name || "",
        document: contact.recipientDocumentNumber || "",
        documentType: contact.recipientDocumentType || "",
        bankName: contact.bankName || "",
        reference: contact.name || "",
        email: contact.recipientEmail || "",
        isOwn: contact.isOwn != null ? String(contact.isOwn) : "0",
    }));
}

/**
 * Parses the amount stored by `RecipientsArrayField` (a `react-number-format` numeric string with a
 * dot decimal separator and no grouping) into a native number.
 *
 * @param {string|number|null|undefined} value
 * @returns {number}
 */
export function parseCredinAmount(value) {
    if (value == null || value === "") {
        return 0;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : 0;
    }
    const parsed = Number(String(value).trim());
    return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Builds the legacy `document` string (`"CUIT 30..."`) from a recipient's type + number.
 *
 * @param {string|undefined|null} documentType
 * @param {string|undefined|null} documentNumber
 * @returns {string}
 */
function buildCredinDocument(documentType, documentNumber) {
    const type = (documentType || "").trim();
    const number = (documentNumber || "").trim();
    if (type && number) {
        return `${type} ${number}`;
    }
    return number || type;
}

/**
 * Maps the Formik `recipients` (UI-kit `RecipientEntry[]`) into the legacy `debin.cbuList` shape
 * consumed by `credin.create.preview` / the summary page. Re-attaches the preview `id` when the
 * recipient already exists in the redux `recipients` snapshot (back-from-summary / draft resume).
 *
 * @param {Array<object>} recipients
 * @param {string} currencyCode legacy account currency code ("0", "2", ...)
 * @param {Array<object>} [reduxRecipients] previous preview recipients (to recover their `id`)
 * @returns {Array<object>}
 */
export function mapRecipientEntriesToCbuList(recipients, currencyCode, reduxRecipients = []) {
    if (!Array.isArray(recipients)) {
        return [];
    }
    return recipients.map((entry) => {
        const quantity = parseCredinAmount(entry.amount);
        const cbuListItem = {
            cbu: entry.cbu,
            document: buildCredinDocument(entry.documentType, entry.document),
            headline: entry.reference || entry.owner || "",
            reference: entry.reference || entry.owner || "",
            email: entry.email || "",
            isOwn: entry.isOwn != null ? String(entry.isOwn) : "0",
            amount: { quantity, amount: quantity, currency: currencyCode },
            comprobante: entry.comprobante != null ? entry.comprobante : "",
            schedule: false,
            hasToShowAgendaDrawer: false,
        };
        const previous = Array.isArray(reduxRecipients)
            ? reduxRecipients.find((recipient) => recipient.cbu === entry.cbu)
            : undefined;
        if (previous && previous.id != null) {
            cbuListItem.id = previous.id;
        }
        return cbuListItem;
    });
}

/**
 * Maps a legacy `debin.cbuList` back into Formik `RecipientEntry[]` (used to pre-fill the form when
 * the user returns from the summary or resumes a draft).
 *
 * @param {Array<object>} cbuList
 * @returns {Array<object>}
 */
export function mapCbuListToRecipientEntries(cbuList) {
    if (!Array.isArray(cbuList)) {
        return [];
    }
    return cbuList.map((item) => ({
        id: item.id != null ? String(item.id) : item.cbu,
        cbu: item.cbu,
        owner: item.headline || item.reference || "",
        document: item.document || "",
        documentType: "",
        bankName: item.bankName || "",
        reference: item.reference || "",
        email: item.email || "",
        isOwn: item.isOwn != null ? String(item.isOwn) : "0",
        source: "agenda",
        amount: item.amount && item.amount.amount != null ? String(item.amount.amount) : null,
        comprobante: item.comprobante != null ? String(item.comprobante) : "",
    }));
}

/**
 * Mirrors the legacy `emptyFieldReceiver` validation: every recipient needs a positive amount and a
 * voucher (comprobante).
 *
 * @param {Array<object>} cbuList
 * @returns {"amount"|"comprobante"|null} the first missing field, or `null` when complete
 */
export function findEmptyCredinRecipientField(cbuList) {
    if (!Array.isArray(cbuList) || cbuList.length === 0) {
        return null;
    }
    for (let index = 0; index < cbuList.length; index += 1) {
        const item = cbuList[index];
        if (!item.amount || !item.amount.amount) {
            return "amount";
        }
        if (!item.comprobante) {
            return "comprobante";
        }
    }
    return null;
}

/**
 * Resolves the concept id from a `SelectFieldV3` value. After the user picks an option the value is
 * the raw `{ value, label }` object; on initial load / back-from-summary it is a plain string id;
 * when nothing is selected it is empty.
 *
 * @param {unknown} concept Formik `concept` value from `CreateCredin`
 * @returns {string} the concept id, or "" when none is selected
 */
export function resolveCredinConceptId(concept) {
    if (concept && typeof concept === "object") {
        const { value } = concept;
        return value != null ? String(value) : "";
    }
    return concept != null ? String(concept) : "";
}

/**
 * Whether Continue must stay inactive: needs a debit account, a concept, at least one recipient and
 * every recipient with amount + voucher filled.
 *
 * @param {Record<string, unknown>} values Formik values from `CreateCredin`
 * @returns {boolean}
 */
export function isCredinContinueInactive(values) {
    const recipients = Array.isArray(values.recipients) ? values.recipients : [];
    if (!values.debitAccount || !resolveCredinConceptId(values.concept) || recipients.length === 0) {
        return true;
    }
    return recipients.some((recipient) => parseCredinAmount(recipient.amount) <= 0 || !recipient.comprobante);
}

/**
 * CVU destinations are identified by the `000` prefix (same as legacy CREDIN totals).
 *
 * @param {string|undefined|null} cbu
 * @returns {boolean}
 */
export function isCredinCvuAccount(cbu) {
    return Boolean(cbu && String(cbu).startsWith("000"));
}

/**
 * Sums recipient amounts split into CBU vs CVU buckets.
 *
 * @param {Array<object>} recipients Formik `RecipientEntry[]` or legacy `cbuList`
 * @returns {{ cbuAmount: number, cvuAmount: number }}
 */
export function getCredinCbuCvuTotals(recipients) {
    if (!Array.isArray(recipients)) {
        return { cbuAmount: 0, cvuAmount: 0 };
    }
    return recipients.reduce(
        (totals, recipient) => {
            const amount = parseCredinAmount(
                recipient && recipient.amount && typeof recipient.amount === "object"
                    ? recipient.amount.amount
                    : recipient && recipient.amount,
            );
            if (isCredinCvuAccount(recipient && recipient.cbu)) {
                totals.cvuAmount += amount;
            } else {
                totals.cbuAmount += amount;
            }
            return totals;
        },
        { cbuAmount: 0, cvuAmount: 0 },
    );
}

/**
 * Mirrors `SelectedAccountsAmount.getConsolidatedDataCredin`: compare CBU/CVU totals against
 * each signature-scheme `limitUsed` (available remaining). Missing types default to `0`.
 *
 * @param {Array<object>} recipients
 * @param {Array<{ type?: string, limitUsed?: number }>} limits
 * @returns {{ cbuExceeded: boolean, cvuExceeded: boolean }}
 */
export function getCredinSignatureLimitErrors(recipients, limits) {
    const limitList = Array.isArray(limits) ? limits : [];
    const cvuLimit = limitList.find((limit) => limit.type === "CVU");
    const cbuLimit = limitList.find((limit) => limit.type === "CBU");
    const availableCvu = (cvuLimit && cvuLimit.limitUsed) || 0;
    const availableCbu = (cbuLimit && cbuLimit.limitUsed) || 0;
    const totals = getCredinCbuCvuTotals(recipients);
    return {
        cbuExceeded: totals.cbuAmount > availableCbu,
        cvuExceeded: totals.cvuAmount > availableCvu,
    };
}

/**
 * @param {Array<object>} recipients
 * @param {Array<object>} limits
 * @param {boolean} enabled `frontend.esquemaFirmas.validation.credinCVU.enable`
 * @returns {boolean}
 */
export function hasCredinSignatureLimitError(recipients, limits, enabled) {
    if (!enabled) {
        return false;
    }
    const errors = getCredinSignatureLimitErrors(recipients, limits);
    return errors.cbuExceeded || errors.cvuExceeded;
}

/**
 * Truncates a decimal to at most two fractional digits (legacy `Limits.getTruncNumber`).
 *
 * @param {number} value
 * @returns {number}
 */
function truncCredinLimitNumber(value) {
    const numberText = value != null ? String(value) : "";
    const index = numberText.indexOf(".");
    if (!numberText || index === -1) {
        return value;
    }
    const end = numberText.length >= index + 3 ? index + 3 : index + 2;
    return parseFloat(numberText.slice(0, end));
}

/**
 * Converts a raw limit quantity to the debit-account currency. USD (`"2"`) is divided by
 * `CMF_Dolar_Valor`, matching `Limits.jsx`.
 *
 * @param {number|string|null|undefined} quantity
 * @param {string|number} currencyCode
 * @returns {number}
 */
export function convertCredinLimitQuantity(quantity, currencyCode) {
    let amount = Number(quantity) || 0;
    if (String(currencyCode) === "2") {
        const dolarQuot = parseFloat(config.get("CMF_Dolar_Valor", "98.90"));
        amount = truncCredinLimitNumber(amount / dolarQuot);
    }
    return amount;
}

/**
 * Clamps a negative available limit to `0` when temporary limit increase is enabled.
 *
 * @param {number} amount
 * @returns {number}
 */
export function clampCredinAvailableLimit(amount) {
    const isTemporaryLimitEnabled = config.getBoolean("frontend.temporarilyincreaselimit.functionalities");
    return isTemporaryLimitEnabled && amount < 0 ? 0 : amount;
}

/**
 * @param {string} value
 * @returns {boolean} true when the value looks like a 22-digit CBU/CVU (else it is treated as alias)
 */
function isCredinCbuOrCvu(value) {
    const digits = String(value || "").replace(/\D/g, "");
    return digits.length === CBU_CVU_LENGTH;
}

/**
 * Promise-based recipient search reused for the drawer `AccountSearcher`, matching the Transfers v3
 * contract (`/get.account.detail`). Resolves the account holder data or rejects with a message.
 *
 * @returns {(value: string) => Promise<{ dataAccount: object, updateData: boolean, documentType: string }>}
 */
export function buildCredinRecipientSearchCallback() {
    return (cbuCvuOrAlias) => {
        const isAccountNumber = isCredinCbuOrCvu(cbuCvuOrAlias);
        const cbuCvu = isAccountNumber ? cbuCvuOrAlias : undefined;
        const alias = isAccountNumber ? undefined : cbuCvuOrAlias;
        return new Promise((resolve, reject) => {
            transferMiddleware
                .getAccountByCbu(cbuCvu, alias)
                .then((response) => {
                    const data = response && response.data && response.data.data;
                    const clientUser = data && data.clientUser;
                    const accountDetail = data && data.accountDetail;
                    const documentType = (data && data.documentType) || "";
                    if (!clientUser && !accountDetail) {
                        reject((response.data && response.data.message) || i18n.get("problems.finding.cbu"));
                        return;
                    }
                    resolve({
                        dataAccount: {
                            owner: clientUser ? clientUser.firstName : "",
                            document: clientUser ? clientUser.cuil : "",
                            documentType,
                            bankName: accountDetail ? accountDetail.bankName : "",
                            cbu: accountDetail ? accountDetail.cbu : cbuCvu,
                        },
                        updateData: false,
                        documentType,
                    });
                })
                .catch(() => {
                    reject(i18n.get("problems.finding.cbu", "No pudimos encontrar la cuenta ingresada."));
                });
        });
    };
}

/**
 * Formats a limit amount for the account-selector helper (`ARS 1.234.567,89`).
 *
 * @param {number|string|null|undefined} quantity
 * @param {string|number} currencyCode legacy currency code
 * @param {{ clampNegativeAvailable?: boolean }} [options]
 * @returns {string}
 */
export function formatCredinLimitAmount(quantity, currencyCode, options = {}) {
    const label = getCredinCurrencyLabel(currencyCode);
    let amount = convertCredinLimitQuantity(quantity, currencyCode);
    if (options.clampNegativeAvailable) {
        amount = clampCredinAvailableLimit(amount);
    }
    const formatted = new Intl.NumberFormat("es-AR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(amount);
    return `${label} ${formatted}`;
}
